import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

async function cloudinaryUpload(file) {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  const fd = new FormData();
  fd.append('file', file);
  fd.append('upload_preset', uploadPreset);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, { method: 'POST', body: fd });
  if (!res.ok) throw new Error(`Cloudinary upload failed: ${res.statusText}`);
  const data = await res.json();
  return data.secure_url;
}

export default function Dashboard() {
  const { signOut } = useAuth();
  const [section, setSection] = useState('photos'); // 'photos' | 'portfolio' | 'settings'

  // ── Photos state ───────────────────────────────────────────────
  const [albums, setAlbums]             = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [photos, setPhotos]             = useState([]);
  const [allTags, setAllTags]           = useState([]);
  const [view, setView]                 = useState('upload');
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDesc, setNewAlbumDesc] = useState('');
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [files, setFiles]               = useState([]);
  const [bulkTags, setBulkTags]         = useState([]);
  const [uploading, setUploading]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [uploadDone, setUploadDone]     = useState(null);
  const [dragOver, setDragOver]         = useState(false);
  const fileInputRef = useRef();

  // ── Portfolio state ────────────────────────────────────────────
  const [videos, setVideos]             = useState([]);
  const [brands, setBrands]             = useState([]);
  const [projects, setProjects]         = useState([]);
  const [portfolioTab, setPortfolioTab] = useState('videos');

  // ── Settings state ─────────────────────────────────────────────
  const [headshotUrl, setHeadshotUrl]   = useState('');
  const [headshotUploading, setHeadshotUploading] = useState(false);
  const [headshotPosition, setHeadshotPosition] = useState({ x: 50, y: 20 });
  const [featuredPhotos, setFeaturedPhotos] = useState([]);
  const [photoCrops, setPhotoCrops]   = useState({});
  const [brandCrops, setBrandCrops]   = useState({});

  useEffect(() => { fetchAlbums(); fetchAllTags(); fetchVideos(); fetchBrands(); fetchProjects(); fetchHeadshot(); fetchFeaturedPhotos(); fetchPhotoCrops(); fetchBrandCrops(); }, []);
  useEffect(() => { if (selectedAlbum) fetchPhotos(selectedAlbum.id); }, [selectedAlbum]);

  // ── Photos ─────────────────────────────────────────────────────
  async function fetchAlbums() {
    const { data } = await supabase.from('albums').select('*').order('created_at', { ascending: false });
    setAlbums(data || []);
  }
  async function fetchAllTags() {
    const { data } = await supabase.from('tags').select('*').order('name');
    setAllTags(data || []);
  }
  async function fetchPhotos(albumId) {
    const { data } = await supabase.from('photos').select('*, photo_tags(tags(name))').eq('album_id', albumId).order('created_at', { ascending: true });
    setPhotos(data || []);
  }
  async function createAlbum(e) {
    e.preventDefault();
    if (!newAlbumName.trim()) return;
    setCreatingAlbum(true);
    const slug = slugify(newAlbumName);
    const { data, error } = await supabase.from('albums').insert({ name: newAlbumName.trim(), slug, description: newAlbumDesc.trim() || null }).select().single();
    if (!error) { setAlbums(prev => [data, ...prev]); setNewAlbumName(''); setNewAlbumDesc(''); }
    setCreatingAlbum(false);
  }
  async function deleteAlbum(id) {
    if (!window.confirm('Delete this album and all its photos?')) return;
    await supabase.from('albums').delete().eq('id', id);
    setAlbums(prev => prev.filter(a => a.id !== id));
    if (selectedAlbum?.id === id) setSelectedAlbum(null);
  }
  function handleDrop(e) {
    e.preventDefault(); setDragOver(false); setUploadDone(null);
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    setFiles(prev => [...prev, ...dropped]);
  }
  function handleFileInput(e) {
    setUploadDone(null);
    const picked = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    setFiles(prev => [...prev, ...picked]);
    e.target.value = '';
  }
  async function handleUpload() {
    if (!selectedAlbum || files.length === 0) return;
    setUploading(true); setUploadDone(null);
    setUploadProgress(files.map(() => 'pending'));
    let successCount = 0, errorCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(prev => { const n = [...prev]; n[i] = 'uploading'; return n; });
      let publicUrl;
      try { publicUrl = await cloudinaryUpload(file); }
      catch { setUploadProgress(prev => { const n = [...prev]; n[i] = 'error'; return n; }); errorCount++; continue; }
      const { data: photo, error: dbError } = await supabase.from('photos').insert({ album_id: selectedAlbum.id, storage_path: '', url: publicUrl, filename: file.name }).select().single();
      if (dbError) { setUploadProgress(prev => { const n = [...prev]; n[i] = 'error'; return n; }); errorCount++; continue; }
      for (const tagName of bulkTags) {
        const { data: tag } = await supabase.from('tags').upsert({ name: tagName }, { onConflict: 'name' }).select().single();
        if (tag) await supabase.from('photo_tags').insert({ photo_id: photo.id, tag_id: tag.id });
      }
      setUploadProgress(prev => { const n = [...prev]; n[i] = 'done'; return n; });
      successCount++;
    }
    setUploading(false); setUploadDone({ success: successCount, errors: errorCount });
    setFiles([]); setBulkTags([]); setUploadProgress([]);
    fetchPhotos(selectedAlbum.id); fetchAllTags();
  }
  async function deletePhoto(photo) {
    await supabase.from('photos').delete().eq('id', photo.id);
    setPhotos(prev => prev.filter(p => p.id !== photo.id));
  }
  async function addTagToPhoto(photoId, tagName) {
    const name = tagName.trim().toLowerCase();
    if (!name) return;
    const { data: tag } = await supabase.from('tags').upsert({ name }, { onConflict: 'name' }).select().single();
    if (tag) { await supabase.from('photo_tags').insert({ photo_id: photoId, tag_id: tag.id }); fetchPhotos(selectedAlbum.id); fetchAllTags(); }
  }
  async function removeTagFromPhoto(photoId, tagName) {
    const { data: tag } = await supabase.from('tags').select('id').eq('name', tagName).single();
    if (tag) { await supabase.from('photo_tags').delete().eq('photo_id', photoId).eq('tag_id', tag.id); fetchPhotos(selectedAlbum.id); }
  }
  async function toggleFeatured(photo) {
    await supabase.from('photos').update({ featured: !photo.featured }).eq('id', photo.id);
    setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, featured: !p.featured } : p));
  }

  // ── Portfolio ──────────────────────────────────────────────────
  async function fetchVideos() {
    const { data } = await supabase.from('portfolio_videos').select('*').order('display_order');
    setVideos(data || []);
  }
  async function fetchBrands() {
    const { data } = await supabase.from('portfolio_brands').select('*').order('display_order');
    setBrands(data || []);
  }
  async function fetchProjects() {
    const { data } = await supabase.from('portfolio_projects').select('*').order('display_order');
    setProjects(data || []);
  }

  // ── Settings ───────────────────────────────────────────────────
  async function fetchFeaturedPhotos() {
    const { data } = await supabase.from('photos').select('id, url, album_id, featured').eq('featured', true).order('created_at', { ascending: false });
    setFeaturedPhotos(data || []);
  }
  async function toggleFeaturedFromSettings(photo) {
    await supabase.from('photos').update({ featured: !photo.featured }).eq('id', photo.id);
    fetchFeaturedPhotos();
    setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, featured: !p.featured } : p));
  }
  async function refreshFeatured() {
    fetchFeaturedPhotos();
  }
  async function fetchHeadshot() {
    const { data } = await supabase.from('site_settings').select('value').eq('key', 'headshot_url').single();
    if (data?.value) setHeadshotUrl(data.value);
    const { data: pos } = await supabase.from('site_settings').select('value').eq('key', 'headshot_position').single();
    if (pos?.value) setHeadshotPosition(JSON.parse(pos.value));
  }
  async function saveHeadshotPosition(pos) {
    setHeadshotPosition(pos);
    await supabase.from('site_settings').upsert({ key: 'headshot_position', value: JSON.stringify(pos) });
  }
  async function uploadHeadshot(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeadshotUploading(true);
    const publicUrl = await cloudinaryUpload(file);
    await supabase.from('site_settings').upsert({ key: 'headshot_url', value: publicUrl });
    setHeadshotUrl(publicUrl);
    setHeadshotUploading(false);
    e.target.value = '';
  }
  async function removeHeadshot() {
    if (!window.confirm('Remove headshot?')) return;
    await supabase.from('site_settings').delete().eq('key', 'headshot_url');
    await supabase.from('site_settings').delete().eq('key', 'headshot_position');
    setHeadshotUrl('');
    setHeadshotPosition({ x: 50, y: 20 });
  }
  async function fetchPhotoCrops() {
    const { data } = await supabase.from('site_settings').select('value').eq('key', 'photo_crops').single();
    if (data?.value) setPhotoCrops(JSON.parse(data.value));
  }
  async function savePhotoCrop(photoId, crop) {
    const updated = { ...photoCrops, [photoId]: crop };
    setPhotoCrops(updated);
    await supabase.from('site_settings').upsert({ key: 'photo_crops', value: JSON.stringify(updated) });
  }
  async function fetchBrandCrops() {
    const { data } = await supabase.from('site_settings').select('value').eq('key', 'brand_crops').single();
    if (data?.value) setBrandCrops(JSON.parse(data.value));
  }
  async function saveBrandCrop(brandId, crop) {
    const updated = { ...brandCrops, [brandId]: crop };
    setBrandCrops(updated);
    await supabase.from('site_settings').upsert({ key: 'brand_crops', value: JSON.stringify(updated) });
  }

  const doneCount = uploadProgress.filter(s => s === 'done').length;

  return (
    <main style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '240px 1fr' }}>

      {/* Sidebar */}
      <aside style={{ borderRight: '1px solid var(--rule)', padding: '1.5rem 0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '0 1.25rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: 500 }}>Admin</span>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/" style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Site</Link>
            <button onClick={signOut} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-faint)', cursor: 'pointer', padding: 0 }}>Out</button>
          </div>
        </div>

        {/* Section tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--rule)', marginBottom: '1rem' }}>
          {['photos', 'portfolio', 'settings', 'copy'].map(s => (
            <button key={s} onClick={() => setSection(s)} style={{
              flex: 1, background: 'none', border: 'none', borderBottom: section === s ? '2px solid var(--ink)' : '2px solid transparent',
              fontSize: '11px', color: section === s ? 'var(--ink)' : 'var(--ink-faint)',
              padding: '0.5rem 0', cursor: 'pointer', textTransform: 'capitalize',
            }}>{s}</button>
          ))}
        </div>

        {section === 'photos' && (
          <>
            <div style={{ padding: '0 1.25rem 1.25rem', borderBottom: '1px solid var(--rule)', marginBottom: '0.75rem' }}>
              <form onSubmit={createAlbum} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <input value={newAlbumName} onChange={e => setNewAlbumName(e.target.value)} placeholder="New roll name" style={inp} />
                <input value={newAlbumDesc} onChange={e => setNewAlbumDesc(e.target.value)} placeholder="Description (optional)" style={inp} />
                <button type="submit" disabled={creatingAlbum} style={{ ...inp, cursor: 'pointer', background: 'var(--ink)', color: 'white', border: 'none' }}>
                  {creatingAlbum ? 'Creating…' : 'Create'}
                </button>
              </form>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {albums.map(album => (
                <div key={album.id} onClick={() => { setSelectedAlbum(album); setUploadDone(null); }} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.6rem 1.25rem', cursor: 'pointer',
                  background: selectedAlbum?.id === album.id ? 'var(--bg-2)' : 'transparent',
                  borderLeft: selectedAlbum?.id === album.id ? '2px solid var(--ink)' : '2px solid transparent',
                }}>
                  <span style={{ fontSize: '13px' }}>{album.name}</span>
                  <button onClick={e => { e.stopPropagation(); deleteAlbum(album.id); }}
                    style={{ background: 'none', border: 'none', color: 'var(--ink-faint)', fontSize: '16px', cursor: 'pointer', lineHeight: 1, padding: '0 0.15rem' }}>×</button>
                </div>
              ))}
              {albums.length === 0 && <p style={{ padding: '0.75rem 1.25rem', fontSize: '12px', color: 'var(--ink-faint)' }}>No rolls yet.</p>}
            </div>
          </>
        )}

        {section === 'portfolio' && (
          <div style={{ padding: '0 1.25rem', fontSize: '12px', color: 'var(--ink-faint)', lineHeight: 1.7 }}>
            <p>Manage videos and brand logos in the main panel →</p>
          </div>
        )}

        {section === 'settings' && (
          <div style={{ padding: '0 1.25rem', fontSize: '12px', color: 'var(--ink-faint)', lineHeight: 1.7 }}>
            <p>Manage site settings in the main panel →</p>
          </div>
        )}
        {section === 'copy' && (
          <div style={{ padding: '0 1.25rem', fontSize: '12px', color: 'var(--ink-faint)', lineHeight: 1.7 }}>
            <p>Edit page copy in the main panel →</p>
          </div>
        )}
      </aside>

      {/* Main */}
      <div style={{ padding: '1.5rem 2rem' }}>

        {/* ── Photos section ── */}
        {section === 'photos' && (
          !selectedAlbum ? (
            <p style={{ fontSize: '13px', color: 'var(--ink-faint)', marginTop: '1rem' }}>Select a roll to upload or manage photos.</p>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '2rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--rule)', paddingBottom: '0.75rem' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, flex: 1 }}>{selectedAlbum.name}</span>
                {['upload', 'manage'].map(v => (
                  <button key={v} onClick={() => setView(v)} style={{
                    background: 'none', border: 'none', fontSize: '12px',
                    color: view === v ? 'var(--ink)' : 'var(--ink-faint)',
                    borderBottom: view === v ? '1px solid var(--ink)' : '1px solid transparent',
                    paddingBottom: '0.2rem', cursor: 'pointer',
                  }}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>
                ))}
              </div>
              {view === 'upload' && (
                <UploadPanel
                  files={files} setFiles={setFiles}
                  bulkTags={bulkTags} setBulkTags={setBulkTags}
                  uploading={uploading} uploadProgress={uploadProgress}
                  uploadDone={uploadDone} setUploadDone={setUploadDone}
                  doneCount={doneCount} totalCount={files.length || uploadProgress.length}
                  dragOver={dragOver} setDragOver={setDragOver}
                  fileInputRef={fileInputRef}
                  handleDrop={handleDrop} handleFileInput={handleFileInput} handleUpload={handleUpload}
                  allTags={allTags}
                />
              )}
              {view === 'manage' && (
                <ManagePanel photos={photos} allTags={allTags} onDeletePhoto={deletePhoto} onAddTag={addTagToPhoto} onRemoveTag={removeTagFromPhoto} onToggleFeatured={toggleFeatured} />
              )}
            </>
          )
        )}

        {/* ── Portfolio section ── */}
        {section === 'portfolio' && (
          <PortfolioPanel
            videos={videos} fetchVideos={fetchVideos}
            brands={brands} fetchBrands={fetchBrands} brandCrops={brandCrops} saveBrandCrop={saveBrandCrop}
            projects={projects} fetchProjects={fetchProjects}
            portfolioTab={portfolioTab} setPortfolioTab={setPortfolioTab}
          />
        )}

        {/* ── Copy section ── */}
        {section === 'copy' && <CopyPanel />}

        {/* ── Settings section ── */}
        {section === 'settings' && (
          <SettingsPanel
            headshotUrl={headshotUrl}
            headshotUploading={headshotUploading}
            uploadHeadshot={uploadHeadshot}
            removeHeadshot={removeHeadshot}
            headshotPosition={headshotPosition}
            saveHeadshotPosition={saveHeadshotPosition}
            featuredPhotos={featuredPhotos}
            onToggleFeatured={toggleFeaturedFromSettings}
            onRefreshFeatured={refreshFeatured}
            albums={albums}
            photoCrops={photoCrops}
            savePhotoCrop={savePhotoCrop}
          />
        )}
      </div>
    </main>
  );
}

// ─── Upload Panel ──────────────────────────────────────────────────────────────
function UploadPanel({ files, setFiles, bulkTags, setBulkTags, uploading, uploadProgress, uploadDone, setUploadDone, doneCount, totalCount, dragOver, setDragOver, fileInputRef, handleDrop, handleFileInput, handleUpload, allTags }) {
  const [newTag, setNewTag] = useState('');

  function toggleBulkTag(name) {
    setBulkTags(prev => prev.includes(name) ? prev.filter(t => t !== name) : [...prev, name]);
  }
  function addNewTag(e) {
    e.preventDefault();
    const t = newTag.trim().toLowerCase();
    if (t && !bulkTags.includes(t)) setBulkTags(prev => [...prev, t]);
    setNewTag('');
  }

  return (
    <div style={{ maxWidth: '560px' }}>
      {uploadDone && (
        <div style={{ marginBottom: '1.25rem', padding: '0.75rem 1rem', background: uploadDone.errors === 0 ? 'var(--bg-2)' : '#fff8f8', border: `1px solid ${uploadDone.errors === 0 ? 'var(--rule)' : '#fcc'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: 'var(--ink)' }}>
            {uploadDone.success} photo{uploadDone.success !== 1 ? 's' : ''} uploaded{uploadDone.errors > 0 ? `, ${uploadDone.errors} failed` : ' successfully'}
          </span>
          <button onClick={() => setUploadDone(null)} style={{ background: 'none', border: 'none', color: 'var(--ink-faint)', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{ border: `1px dashed ${dragOver ? 'var(--ink)' : 'var(--rule)'}`, background: dragOver ? 'var(--bg-2)' : 'transparent', padding: '2.5rem', textAlign: 'center', cursor: 'pointer', marginBottom: '1.25rem', transition: 'border-color 150ms, background 150ms' }}
      >
        <p style={{ fontSize: '13px', color: 'var(--ink-faint)' }}>Drop photos here or click to browse</p>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginTop: '0.3rem' }}>Multiple files · JPEG, PNG, TIFF, HEIC</p>
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileInput} />
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ fontSize: '11px', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.6rem' }}>Tags — applied to all photos in this upload</label>
        {allTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {allTags.map(tag => {
              const active = bulkTags.includes(tag.name);
              return (
                <button key={tag.id} onClick={() => toggleBulkTag(tag.name)} style={{ background: active ? 'var(--ink)' : 'transparent', color: active ? 'white' : 'var(--ink-faint)', border: `1px solid ${active ? 'var(--ink)' : 'var(--rule)'}`, padding: '0.25rem 0.6rem', fontSize: '11px', cursor: 'pointer', transition: 'background 150ms, color 150ms, border-color 150ms' }}>
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
        <form onSubmit={addNewTag} style={{ display: 'flex', gap: '0.5rem' }}>
          <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add new tag…" style={{ ...inp, flex: 1 }} />
          <button type="submit" style={{ ...inp, width: 'auto', padding: '0.45rem 0.75rem', cursor: 'pointer', flexShrink: 0 }}>Add</button>
        </form>
        {bulkTags.length > 0 && (
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {bulkTags.map(t => (
              <span key={t} style={{ fontSize: '11px', background: 'var(--ink)', color: 'white', padding: '0.2rem 0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                {t}
                <button onClick={() => setBulkTags(prev => prev.filter(x => x !== t))} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 0, fontSize: '12px', lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '0.5rem' }}>{files.length} {files.length === 1 ? 'file' : 'files'} queued</p>
          <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {files.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '0.4rem 0.6rem', background: 'var(--bg-2)', gap: '0.5rem' }}>
                <span style={{ fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: statusColor(uploadProgress[i]) }}>{f.name}</span>
                <span style={{ fontSize: '11px', color: 'var(--ink-faint)', flexShrink: 0 }}>{statusLabel(uploadProgress[i])}</span>
                {!uploading && <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--ink-faint)', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={handleUpload} disabled={uploading || files.length === 0} style={{ padding: '0.6rem 1.25rem', border: 'none', fontSize: '13px', background: files.length > 0 && !uploading ? 'var(--ink)' : 'var(--bg-3)', color: files.length > 0 && !uploading ? 'white' : 'var(--ink-faint)', cursor: files.length > 0 && !uploading ? 'pointer' : 'default' }}>
        {uploading ? `Uploading ${doneCount + 1} / ${uploadProgress.length}…` : `Upload ${files.length > 0 ? files.length + ' ' : ''}photo${files.length !== 1 ? 's' : ''}`}
      </button>
    </div>
  );
}

// ─── Manage Panel ──────────────────────────────────────────────────────────────
function ManagePanel({ photos, allTags, onDeletePhoto, onAddTag, onRemoveTag, onToggleFeatured }) {
  const [tagInputs, setTagInputs] = useState({});

  if (photos.length === 0) return <p style={{ fontSize: '13px', color: 'var(--ink-faint)' }}>No photos in this roll yet.</p>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
      {photos.map(photo => {
        const tags = photo.photo_tags?.map(pt => pt.tags?.name).filter(Boolean) ?? [];
        return (
          <div key={photo.id} style={{ border: '1px solid var(--rule)' }}>
            <div style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden' }}>
              <img src={photo.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <button onClick={() => onDeletePhoto(photo)} style={{ position: 'absolute', top: '0.35rem', right: '0.35rem', background: 'rgba(255,255,255,0.9)', border: 'none', color: 'var(--ink)', width: '20px', height: '20px', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
              <button onClick={() => onToggleFeatured(photo)} title={photo.featured ? 'Remove from home' : 'Feature on home'} style={{ position: 'absolute', top: '0.35rem', left: '0.35rem', background: 'rgba(255,255,255,0.9)', border: 'none', width: '20px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: photo.featured ? 'var(--ink)' : 'var(--ink-faint)' }}>★</button>
            </div>
            <div style={{ padding: '0.5rem' }}>
              {tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.4rem' }}>
                  {tags.map(tag => (
                    <span key={tag} onClick={() => onRemoveTag(photo.id, tag)} title="Remove" style={{ fontSize: '10px', background: 'var(--ink)', color: 'white', padding: '0.15rem 0.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {tag} <span style={{ opacity: 0.6 }}>×</span>
                    </span>
                  ))}
                </div>
              )}
              {allTags.filter(t => !tags.includes(t.name)).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.4rem' }}>
                  {allTags.filter(t => !tags.includes(t.name)).map(tag => (
                    <span key={tag.id} onClick={() => onAddTag(photo.id, tag.name)} title="Add" style={{ fontSize: '10px', border: '1px solid var(--rule)', color: 'var(--ink-faint)', padding: '0.15rem 0.4rem', cursor: 'pointer' }}>
                      + {tag.name}
                    </span>
                  ))}
                </div>
              )}
              <form onSubmit={e => { e.preventDefault(); const v = tagInputs[photo.id] || ''; if (v) { onAddTag(photo.id, v); setTagInputs(prev => ({ ...prev, [photo.id]: '' })); } }} style={{ display: 'flex', gap: '0.3rem' }}>
                <input value={tagInputs[photo.id] || ''} onChange={e => setTagInputs(prev => ({ ...prev, [photo.id]: e.target.value }))} placeholder="New tag…" style={{ ...inp, flex: 1, fontSize: '11px', padding: '0.3rem 0.4rem' }} />
                <button type="submit" style={{ ...inp, padding: '0.3rem 0.5rem', cursor: 'pointer', flexShrink: 0, width: 'auto' }}>+</button>
              </form>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Portfolio Panel ───────────────────────────────────────────────────────────
function PortfolioPanel({ videos, fetchVideos, brands, fetchBrands, brandCrops, saveBrandCrop, projects, fetchProjects, portfolioTab, setPortfolioTab }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--rule)', paddingBottom: '0.75rem' }}>
        {['videos', 'projects', 'brands'].map(t => (
          <button key={t} onClick={() => setPortfolioTab(t)} style={{ background: 'none', border: 'none', fontSize: '12px', color: portfolioTab === t ? 'var(--ink)' : 'var(--ink-faint)', borderBottom: portfolioTab === t ? '1px solid var(--ink)' : '1px solid transparent', paddingBottom: '0.2rem', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>
      {portfolioTab === 'videos'   && <VideoManager videos={videos} fetchVideos={fetchVideos} />}
      {portfolioTab === 'projects' && <ProjectManager projects={projects} fetchProjects={fetchProjects} />}
      {portfolioTab === 'brands'   && <BrandManager brands={brands} fetchBrands={fetchBrands} brandCrops={brandCrops} saveBrandCrop={saveBrandCrop} />}
    </div>
  );
}

function VideoManager({ videos, fetchVideos }) {
  const [form, setForm] = useState({ title: '', vimeo_url: '', description: '', responsibilities: '', category: 'commercial' });
  const [thumbFile, setThumbFile] = useState(null);
  const thumbInputRef = useRef();
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingThumbId, setUploadingThumbId] = useState(null);

  function resetForm() {
    setForm({ title: '', vimeo_url: '', description: '', responsibilities: '', category: 'commercial' });
    setThumbFile(null);
    if (thumbInputRef.current) thumbInputRef.current.value = '';
    setEditId(null);
  }

  async function moveVideoTo(fromIndex, toIndex) {
    if (toIndex === fromIndex) return;
    const reordered = [...videos];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    await Promise.all(
      reordered.map((v, i) => supabase.from('portfolio_videos').update({ display_order: i }).eq('id', v.id))
    );
    await fetchVideos();
  }

  async function uploadThumb(file) {
    try { return await cloudinaryUpload(file); }
    catch (e) { alert(`Thumbnail upload failed: ${e.message}`); return null; }
  }

  async function saveVideo(e) {
    e.preventDefault();
    if (!form.title || !form.vimeo_url) return;
    setSaving(true);
    let thumbnail_url = undefined;
    if (thumbFile) {
      thumbnail_url = await uploadThumb(thumbFile);
      if (!thumbnail_url) { setSaving(false); return; }
    }
    const payload = {
      title: form.title, vimeo_url: form.vimeo_url,
      description: form.description || null, responsibilities: form.responsibilities || null, category: form.category,
      ...(!editId && { display_order: videos.length }),
      ...(thumbnail_url !== undefined && { thumbnail_url }),
    };
    let error;
    if (editId) {
      ({ error } = await supabase.from('portfolio_videos').update(payload).eq('id', editId));
    } else {
      ({ error } = await supabase.from('portfolio_videos').insert(payload));
    }
    if (error) {
      alert(`Failed to save video: ${error.message}`);
      setSaving(false);
      return;
    }
    await fetchVideos();
    resetForm();
    setSaving(false);
  }

  async function uploadThumbForVideo(video, file, inputEl) {
    setUploadingThumbId(video.id);
    let publicUrl;
    try { publicUrl = await cloudinaryUpload(file); }
    catch (e) { alert(`Upload failed: ${e.message}`); setUploadingThumbId(null); return; }
    const { error: dbErr } = await supabase.from('portfolio_videos').update({ thumbnail_url: publicUrl }).eq('id', video.id);
    if (dbErr) { alert(`Save failed: ${dbErr.message}`); setUploadingThumbId(null); return; }
    if (inputEl) inputEl.value = '';
    await fetchVideos();
    setUploadingThumbId(null);
  }

  async function deleteVideo(id) {
    if (!window.confirm('Delete this video?')) return;
    await supabase.from('portfolio_videos').delete().eq('id', id);
    fetchVideos();
  }

  function startEdit(v) {
    setForm({ title: v.title, vimeo_url: v.vimeo_url, description: v.description || '', responsibilities: v.responsibilities || '', category: v.category || 'commercial' });
    setThumbFile(null);
    setEditId(v.id);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '900px' }}>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>{editId ? 'Editing video' : 'Add video'}</p>
        <form onSubmit={saveVideo} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Title *" style={inp} required />
          <input value={form.vimeo_url} onChange={e => setForm(f => ({ ...f, vimeo_url: e.target.value }))} placeholder="Vimeo URL * (e.g. https://vimeo.com/123456)" style={inp} required />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" style={{ ...inp, height: '80px', resize: 'vertical' }} />
          <textarea value={form.responsibilities} onChange={e => setForm(f => ({ ...f, responsibilities: e.target.value }))} placeholder="Responsibilities" style={{ ...inp, height: '80px', resize: 'vertical' }} />
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inp}>
            <option value="commercial">Commercial</option>
            <option value="social">Social First</option>
          </select>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.3rem' }}>Thumbnail (16:9 image)</label>
            <input ref={thumbInputRef} type="file" accept="image/*" onChange={e => setThumbFile(e.target.files?.[0] || null)} style={{ fontSize: '12px', color: 'var(--ink-mid)' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>{saving ? 'Saving…' : editId ? 'Update' : 'Add'}</button>
            {editId && <button type="button" onClick={resetForm} style={{ ...inp, width: 'auto', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>Cancel</button>}
          </div>
        </form>
      </div>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>{videos.length} video{videos.length !== 1 ? 's' : ''}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {videos.map(v => (
            <div key={v.id} style={{ border: '1px solid var(--rule)', padding: '0.75rem' }}>
              {v.thumbnail_url && (
                <img src={v.thumbnail_url} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', marginBottom: '0.5rem' }} />
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '13px', fontWeight: 500, marginBottom: '0.2rem' }}>{v.title}</p>
                  <p style={{ fontSize: '11px', color: 'var(--ink-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vimeo_url}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center' }}>
                  <select
                    value={videos.indexOf(v)}
                    onChange={e => moveVideoTo(videos.indexOf(v), Number(e.target.value))}
                    title="Move to position"
                    style={{ fontSize: '11px', color: 'var(--ink-faint)', border: '1px solid var(--rule)', background: 'var(--bg)', padding: '2px 4px', cursor: 'pointer' }}
                  >
                    {videos.map((_, i) => (
                      <option key={i} value={i}>{i + 1} of {videos.length}</option>
                    ))}
                  </select>
                  <button onClick={() => startEdit(v)} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-faint)', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => deleteVideo(v.id)} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-faint)', cursor: 'pointer' }}>×</button>
                </div>
              </div>
              <label style={{ fontSize: '11px', color: uploadingThumbId === v.id ? 'var(--ink)' : 'var(--ink-faint)', cursor: 'pointer', display: 'inline-block', marginTop: '0.4rem' }}>
                {uploadingThumbId === v.id ? 'Uploading…' : v.thumbnail_url ? 'Replace thumbnail' : 'Upload thumbnail'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={!!uploadingThumbId}
                  onChange={e => { if (e.target.files?.[0]) uploadThumbForVideo(v, e.target.files[0], e.target); }}
                />
              </label>
            </div>
          ))}
          {videos.length === 0 && <p style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>No videos yet.</p>}
        </div>
      </div>
    </div>
  );
}

function ProjectManager({ projects, fetchProjects }) {
  const blank = { category: '', headline: '', description: '', tech: '', status: 'live', display_order: 0 };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  function resetForm() { setForm(blank); setEditId(null); }

  async function saveProject(e) {
    e.preventDefault();
    if (!form.headline || !form.category) return;
    setSaving(true);
    const payload = {
      category: form.category,
      headline: form.headline,
      description: form.description || null,
      tech: form.tech || null,
      status: form.status,
      display_order: Number(form.display_order) || 0,
    };
    if (editId) {
      await supabase.from('portfolio_projects').update(payload).eq('id', editId);
    } else {
      await supabase.from('portfolio_projects').insert(payload);
    }
    await fetchProjects();
    resetForm();
    setSaving(false);
  }

  async function deleteProject(id) {
    if (!window.confirm('Delete this project?')) return;
    await supabase.from('portfolio_projects').delete().eq('id', id);
    fetchProjects();
  }

  function startEdit(p) {
    setForm({ category: p.category, headline: p.headline, description: p.description || '', tech: p.tech || '', status: p.status, display_order: p.display_order });
    setEditId(p.id);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '900px' }}>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>{editId ? 'Editing project' : 'Add project'}</p>
        <form onSubmit={saveProject} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Category * (e.g. Automation Tool)" style={inp} required />
          <input value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} placeholder="Headline * (what it does, not its name)" style={inp} required />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" style={{ ...inp, height: '80px', resize: 'vertical' }} />
          <input value={form.tech} onChange={e => setForm(f => ({ ...f, tech: e.target.value }))} placeholder="Tech stack (comma-separated, e.g. React, Supabase)" style={inp} />
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inp}>
            <option value="live">Live</option>
            <option value="in_development">In development</option>
            <option value="shipped">Shipped</option>
          </select>
          <input value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: e.target.value }))} placeholder="Order" type="number" style={{ ...inp, width: '120px' }} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>{saving ? 'Saving…' : editId ? 'Update' : 'Add'}</button>
            {editId && <button type="button" onClick={resetForm} style={{ ...inp, width: 'auto', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>Cancel</button>}
          </div>
        </form>
      </div>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>{projects.length} project{projects.length !== 1 ? 's' : ''}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {projects.map(p => (
            <div key={p.id} style={{ border: '1px solid var(--rule)', padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '0.15rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{p.category}</p>
                <p style={{ fontSize: '13px', fontWeight: 500 }}>{p.headline}</p>
                <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginTop: '0.15rem' }}>{p.status}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button onClick={() => startEdit(p)} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-faint)', cursor: 'pointer' }}>Edit</button>
                <button onClick={() => deleteProject(p.id)} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-faint)', cursor: 'pointer' }}>×</button>
              </div>
            </div>
          ))}
          {projects.length === 0 && <p style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>No projects yet.</p>}
        </div>
      </div>
    </div>
  );
}

function BrandManager({ brands, fetchBrands, brandCrops, saveBrandCrop }) {
  const [name, setName] = useState('');
  const [order, setOrder] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const logoInputRef = useRef();

  async function addBrand(e) {
    e.preventDefault();
    if (!name) return;
    setSaving(true);
    let logo_url = null;
    if (logoFile) {
      try { logo_url = await cloudinaryUpload(logoFile); }
      catch (e) { alert(`Upload failed: ${e.message}`); setSaving(false); return; }
    }
    await supabase.from('portfolio_brands').insert({ name, logo_url, display_order: Number(order) || 0 });
    await fetchBrands();
    setName(''); setOrder(0); setLogoFile(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
    setSaving(false);
  }

  async function uploadLogo(brand, file, inputEl) {
    setUploadingId(brand.id);
    let publicUrl;
    try { publicUrl = await cloudinaryUpload(file); }
    catch (e) { alert(`Upload failed: ${e.message}`); setUploadingId(null); return; }
    const { error: dbError } = await supabase.from('portfolio_brands').update({ logo_url: publicUrl }).eq('id', brand.id);
    if (dbError) { alert(`Save failed: ${dbError.message}`); setUploadingId(null); return; }
    if (inputEl) inputEl.value = '';
    await fetchBrands();
    setUploadingId(null);
  }

  async function deleteBrand(id) {
    await supabase.from('portfolio_brands').delete().eq('id', id);
    fetchBrands();
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '900px' }}>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>Add brand</p>
        <form onSubmit={addBrand} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Brand name *" style={inp} required />
          <div>
            <label style={{ fontSize: '11px', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.3rem' }}>Logo (PNG/SVG, transparent bg)</label>
            <input ref={logoInputRef} type="file" accept="image/*" onChange={e => setLogoFile(e.target.files?.[0] || null)} style={{ fontSize: '12px', color: 'var(--ink-mid)' }} />
          </div>
          <input value={order} onChange={e => setOrder(e.target.value)} placeholder="Order" type="number" style={{ ...inp, width: '120px' }} />
          <button type="submit" disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>{saving ? 'Saving…' : 'Add brand'}</button>
        </form>
      </div>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>{brands.length} brand{brands.length !== 1 ? 's' : ''}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {brands.map(b => {
            const crop = brandCrops[b.id] || { x: 50, y: 50, scale: 1 };
            return (
              <div key={b.id} style={{ border: '1px solid var(--rule)', padding: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <p style={{ fontSize: '12px', color: 'var(--ink)' }}>{b.name}</p>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <label style={{ fontSize: '11px', color: uploadingId === b.id ? 'var(--ink)' : 'var(--ink-faint)', cursor: 'pointer' }}>
                      {uploadingId === b.id ? 'uploading…' : b.logo_url ? 'replace logo' : 'upload logo'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingId === b.id} onChange={e => { if (e.target.files?.[0]) uploadLogo(b, e.target.files[0], e.target); }} />
                    </label>
                    <button onClick={() => deleteBrand(b.id)} style={{ background: 'none', border: 'none', fontSize: '11px', color: 'var(--ink-faint)', cursor: 'pointer' }}>remove</button>
                  </div>
                </div>
                {b.logo_url && (
                  <PhotoCropEditor
                    url={b.logo_url}
                    crop={crop}
                    W={200} H={56}
                    contain
                    onChange={c => saveBrandCrop(b.id, c)}
                  />
                )}
              </div>
            );
          })}
          {brands.length === 0 && <p style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>No brands yet.</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Headshot Positioner ───────────────────────────────────────────────────────
function HeadshotPositioner({ url, position, onChange }) {
  const containerRef = useRef();
  const dragging = useRef(false);
  const SIZE = 180;

  function getPos(e) {
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.round(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
    const y = Math.round(Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)));
    return { x, y };
  }

  function onStart(e) { e.preventDefault(); dragging.current = true; onChange(getPos(e)); }
  function onMove(e)  { if (!dragging.current) return; e.preventDefault(); onChange(getPos(e)); }
  function onEnd()    { dragging.current = false; }

  return (
    <div>
      <div
        ref={containerRef}
        onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
        onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        style={{
          width: SIZE, height: SIZE, borderRadius: '50%', overflow: 'hidden',
          cursor: 'grab', userSelect: 'none', border: '1px solid var(--rule)',
        }}
      >
        <img
          src={url} alt="Headshot"
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${position.x}% ${position.y}%`, display: 'block', pointerEvents: 'none' }}
        />
      </div>
      <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginTop: '0.5rem' }}>
        Position: {position.x}% / {position.y}%
      </p>
    </div>
  );
}

// ─── Photo Crop Editor ─────────────────────────────────────────────────────────
function PhotoCropEditor({ url, crop, onChange, W = 240, H = 135, contain = false }) {
  const ref = useRef();
  const dragging = useRef(false);
  const { x, y, scale } = crop;

  function getPos(e) {
    const rect = ref.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: Math.round(Math.max(0, Math.min(100, ((cx - rect.left) / rect.width) * 100))),
      y: Math.round(Math.max(0, Math.min(100, ((cy - rect.top) / rect.height) * 100))),
    };
  }

  function onStart(e) { e.preventDefault(); dragging.current = true; onChange({ ...crop, ...getPos(e) }); }
  function onMove(e)  { if (!dragging.current) return; e.preventDefault(); onChange({ ...crop, ...getPos(e) }); }
  function onEnd()    { dragging.current = false; }

  return (
    <div>
      <div
        ref={ref}
        onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
        onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        style={{ width: W, height: H, overflow: 'hidden', cursor: 'crosshair', userSelect: 'none', border: '1px solid var(--rule)', position: 'relative', flexShrink: 0 }}
      >
        <img src={url} alt="" draggable={false} style={{
          width: '100%', height: '100%', objectFit: contain ? 'contain' : 'cover',
          objectPosition: `${x}% ${y}%`,
          transform: `scale(${scale})`,
          transformOrigin: `${x}% ${y}%`,
          display: 'block', pointerEvents: 'none',
        }} />
        {/* Crosshair */}
        <div style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: '50%', left: '-8px', right: '-8px', height: '1px', background: 'rgba(255,255,255,0.9)' }} />
          <div style={{ position: 'absolute', left: '50%', top: '-8px', bottom: '-8px', width: '1px', background: 'rgba(255,255,255,0.9)' }} />
        </div>
      </div>
      <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '10px', color: 'var(--ink-faint)', letterSpacing: '0.05em', flexShrink: 0 }}>ZOOM</span>
        <input type="range" min={contain ? 50 : 100} max="250" step="5" value={Math.round(scale * 100)}
          onChange={e => onChange({ ...crop, scale: Number(e.target.value) / 100 })}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: '10px', color: 'var(--ink-faint)', width: '36px', textAlign: 'right', flexShrink: 0 }}>{Math.round(scale * 100)}%</span>
      </div>
      <p style={{ fontSize: '10px', color: 'var(--ink-faint)', marginTop: '0.35rem' }}>
        Drag to reposition · {x}% / {y}%
      </p>
    </div>
  );
}

// ─── Settings Panel ────────────────────────────────────────────────────────────
function SettingsPanel({ headshotUrl, headshotUploading, uploadHeadshot, removeHeadshot, headshotPosition, saveHeadshotPosition, featuredPhotos, onToggleFeatured, onRefreshFeatured, albums, photoCrops, savePhotoCrop }) {
  const [browseAlbumId, setBrowseAlbumId] = useState(null);
  const [browsePhotos, setBrowsePhotos] = useState([]);
  const [loadingBrowse, setLoadingBrowse] = useState(false);

  async function loadAlbumPhotos(albumId) {
    setBrowseAlbumId(albumId);
    setLoadingBrowse(true);
    const { data } = await supabase.from('photos').select('id, url, featured').eq('album_id', albumId).order('created_at', { ascending: true });
    setBrowsePhotos(data || []);
    setLoadingBrowse(false);
  }

  async function toggleFromBrowse(photo) {
    await supabase.from('photos').update({ featured: !photo.featured }).eq('id', photo.id);
    setBrowsePhotos(prev => prev.map(p => p.id === photo.id ? { ...p, featured: !p.featured } : p));
    onRefreshFeatured();
  }

  return (
    <div style={{ maxWidth: '680px' }}>
      <p style={{ fontSize: '13px', fontWeight: 500, marginBottom: '1.5rem' }}>Site Settings</p>

      {/* Headshot */}
      <div style={{ marginBottom: '2.5rem', paddingBottom: '2.5rem', borderBottom: '1px solid var(--rule)' }}>
        <label style={{ fontSize: '11px', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Headshot</label>
        {headshotUrl && (
          <HeadshotPositioner
            url={headshotUrl}
            position={headshotPosition}
            onChange={saveHeadshotPosition}
          />
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: headshotUrl ? '0.75rem' : 0 }}>
          <label style={{ ...inp, width: 'auto', padding: '0.5rem 1rem', cursor: headshotUploading ? 'default' : 'pointer', display: 'inline-block', opacity: headshotUploading ? 0.5 : 1 }}>
            {headshotUploading ? 'Uploading…' : headshotUrl ? 'Replace headshot' : 'Upload headshot'}
            <input type="file" accept="image/*" onChange={uploadHeadshot} disabled={headshotUploading} style={{ display: 'none' }} />
          </label>
          {headshotUrl && (
            <button onClick={removeHeadshot} style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-faint)', cursor: 'pointer', padding: '0.5rem 0' }}>Remove</button>
          )}
        </div>
        <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginTop: '0.5rem' }}>Drag the image to reposition. Appears on the Careers landing page.</p>
      </div>

      {/* Home page photos */}
      <div>
        <label style={{ fontSize: '11px', color: 'var(--ink-faint)', display: 'block', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Home page photos</label>
        <p style={{ fontSize: '12px', color: 'var(--ink-faint)', marginBottom: '1rem' }}>
          Starred photos rotate on the home page. Pick an album below to star or unstar photos.
          {featuredPhotos.length > 0 && ` ${featuredPhotos.length} currently selected.`}
        </p>

        {/* Currently featured — with crop editors */}
        {featuredPhotos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {featuredPhotos.map((p, i) => (
              <div key={p.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>Photo {i + 1}</span>
                  <button onClick={() => onToggleFeatured(p)} style={{ background: 'none', border: 'none', fontSize: '11px', color: 'var(--ink-faint)', cursor: 'pointer', padding: 0 }}>Remove</button>
                </div>
                <PhotoCropEditor
                  url={p.url}
                  crop={photoCrops[p.id] || { x: 50, y: 50, scale: 1 }}
                  onChange={crop => savePhotoCrop(p.id, crop)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Album picker */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
          {albums.map(a => (
            <button key={a.id} onClick={() => loadAlbumPhotos(a.id)} style={{
              background: browseAlbumId === a.id ? 'var(--ink)' : 'transparent',
              color: browseAlbumId === a.id ? 'white' : 'var(--ink-faint)',
              border: `1px solid ${browseAlbumId === a.id ? 'var(--ink)' : 'var(--rule)'}`,
              padding: '0.25rem 0.6rem', fontSize: '11px', cursor: 'pointer',
            }}>{a.name}</button>
          ))}
        </div>

        {/* Browse grid */}
        {loadingBrowse && <p style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Loading…</p>}
        {!loadingBrowse && browsePhotos.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.4rem' }}>
            {browsePhotos.map(p => (
              <div key={p.id} onClick={() => toggleFromBrowse(p)} title={p.featured ? 'Remove from home' : 'Add to home'} style={{ position: 'relative', aspectRatio: '1', cursor: 'pointer', opacity: p.featured ? 1 : 0.55, transition: 'opacity 150ms' }}>
                <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', border: p.featured ? '2px solid var(--ink)' : '1px solid var(--rule)' }} />
                {p.featured && (
                  <div style={{ position: 'absolute', top: '3px', left: '3px', fontSize: '10px', background: 'var(--ink)', color: 'white', padding: '1px 4px', lineHeight: 1.4 }}>★</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function statusColor(s) { return s === 'done' ? '#999' : s === 'error' ? '#e88' : s === 'uploading' ? 'var(--ink)' : 'var(--ink-mid)'; }
function statusLabel(s) { return s === 'done' ? '✓' : s === 'error' ? 'error' : s === 'uploading' ? '…' : ''; }

const inp = {
  background: 'white', border: '1px solid var(--rule)', color: 'var(--ink)',
  padding: '0.45rem 0.6rem', fontSize: '13px', outline: 'none', width: '100%',
};

// ─── Copy Panel ────────────────────────────────────────────────────────────────
const COPY_SCHEMA = {
  home: [
    { key: 'hero_headline',      label: 'Hero headline',      multiline: true,  hint: 'New line = line break',
      def: 'Your creative partner for commercial film\nand content.' },
    { key: 'hero_subtext',       label: 'Hero subtext',
      def: 'End-to-end creative production and film editing for brands and agencies.' },
    { key: 'statement_headline', label: 'Statement headline',
      def: 'From script to delivery, films built to perform.' },
    { key: 'statement_body',     label: 'Statement body',     multiline: true,
      def: 'Drawing on years of experience within an award-winning film production company and a global media agency, the studio brings together world-class craft with a deep understanding of audiences, channels, and what makes content truly land.' },
    { key: 'section_work',       label: 'Section label — Selected Work',  def: 'Selected Work' },
    { key: 'section_brands',     label: 'Section label — Brands',         def: "Brands I've Worked With" },
  ],
  about: [
    { key: 'headline',       label: 'Page headline',               def: 'Filmmaker and creative producer.' },
    { key: 'location',       label: 'Location line',               def: 'Based in Lisbon. Available for projects across Europe and beyond.' },
    { key: 'story_left',     label: 'Story — left column',         multiline: true, hint: 'Blank line between paragraphs',
      def: "Having worked across media agencies managing accounts for some of the world's biggest brands, I developed a deep understanding of how audiences think, how channels work, and how budgets need to perform.\n\nThat foundation led me to Banjoman, Ireland's leading production company, where as Head of The Studio I honed my craft across every stage of creative production. Producing, directing, and editing commercial films for some of Ireland's most recognised brands, I learned what it takes to make work that's both creatively strong and commercially effective." },
    { key: 'story_right',    label: 'Story — right column',        multiline: true,
      def: "Liam Boyd Creative Studio was born when I relocated to Lisbon and stepped out on my own. The studio exists to bring that same level of craft and strategic thinking to brands and agencies globally. World-class commercial film, built around your brief." },
    { key: 'approach_left',  label: 'Approach — left column',      multiline: true,
      def: "I can work with you at any stage of the production process. If you've just received a brief, if you have an idea but aren't sure where to start, or if you've already shot something beautiful and need someone to bring it to life, I'm here.\n\nMy clients can expect award-winning production quality, alongside an understanding that creative productions sometimes need to be lean and agile. Having produced large multi-week productions across multiple countries as well as super-localised social-first content, you're covered whatever the scale." },
    { key: 'approach_right', label: 'Approach — right column',     multiline: true,
      def: "The most important thing is the work. Delivering high-quality, well-crafted creative is always the priority. But working with genuinely good people, people you want to come back to again and again, is a close second.\n\nBeing the steward of your brand is an honour, and I treat it as one. Your brand is in good hands here." },
  ],
  services: [
    { key: 'headline',        label: 'Page headline',              def: 'Independent creative studio for commercial film.' },
    { key: 'intro',           label: 'Intro paragraph',            multiline: true,
      def: "I work directly with brands and agencies, from the first brief through to finished assets, or stepping in wherever the project needs it." },
    { key: 'rtb',             label: 'Reason to believe',
      def: 'Four years as a creative producer at an award-winning commercial film production company.' },
    { key: 'rates',           label: 'Rates note',                 multiline: true,
      def: 'Rates vary depending on scope, duration, and the nature of the work. Please get in touch and I can discuss what makes sense.' },
  ],
  contact: [
    { key: 'headline',     label: 'Page headline',      def: "Let's make something." },
    { key: 'location',     label: 'Location',           def: 'Lisbon, Portugal' },
    { key: 'availability', label: 'Availability line',  def: 'Available for remote and on-location projects globally' },
  ],
  work: [
    { key: 'label_commercial', label: 'Commercial section label', def: 'Commercial Work' },
    { key: 'label_social',     label: 'Social First section label', def: 'Social First' },
  ],
  careers: [
    { key: 'tagline',   label: 'Tagline (above name)',   def: 'Commercial Marketing Professional' },
    { key: 'bio',       label: 'Bio paragraph',          multiline: true,
      def: 'I have more than six years experience across international media strategy, commercial account management, film & video editing, and creative production from pitch through to delivery.\nAvailable for senior opportunities across Europe.' },
    { key: 'location',  label: 'Location line',          def: 'Lisbon, PT / Dublin, IE' },
    { key: 'cv_desc',   label: 'CV link description',    def: 'Full work history, competencies and education' },
    { key: 'brief_desc',label: 'Role Brief link description', def: "Who I am, what I've done, and what I'm looking for" },
    { key: 'portfolio_desc', label: 'Portfolio link description', def: 'Selected commercial work and brand experience' },
  ],
  cv: [
    { key: 'name',        label: 'Name',           def: 'Liam Boyd' },
    { key: 'title',       label: 'Title',          def: 'Commercial Marketing Professional' },
    { key: 'subtitle',    label: 'Subtitle',       def: 'Multi-Market Media Strategy · Revenue Growth · Creative Production' },
    { key: 'location',    label: 'Location',       def: 'Lisbon, PT / Dublin, IE' },
    { key: 'email',       label: 'Email',          def: 'liam@liamboyd.com' },
    { key: 'phone',       label: 'Phone',          def: '+353 86 129 1030 / +351 910 622 350' },
    { key: 'profile',     label: 'Profile paragraph', multiline: true, def: "I'm a senior commercial marketing professional with 6+ years of experience contributing to growth across international markets. Experienced operating where marketing strategy and commercial execution meet, having worked within teams managing seven-figure media investment budgets within portfolios with activity across up to 10 European markets, while building independent experience in new business development and end-to-end creative production.\n\nI'm equally comfortable in agency, brand, and production environments, and I'm able to bring structure to complex multi-stakeholder situations, supporting senior leaders in delivering against commercial objectives, and taking ownership of execution from brief through to delivery. First-class Honours in Marketing. Currently based in Lisbon, PT with an address in Dublin, Ireland." },
    { key: 'footer_note', label: 'Footer note',    def: 'Open to conversations across Europe · Permanent roles or contract opportunities' },
  ],
  brief: [
    { key: 'who',             label: 'Who I am (opening section)', multiline: true, def: "I'm a commercial marketing professional with 6+ years of experience across international media strategy, account management, and end-to-end creative production. I've spent my career operating at the intersection of marketing execution and commercial accountability, contributing to seven-figure media campaigns across up to 10 European markets at Mediacom, one of the world's leading media agencies, and leading commercial productions with creative governance while independently generating new business revenue at Banjoman Commercials.\n\nWhat makes me distinctive is the range of professional environments I've worked in. I understand how campaigns are built and bought, how production budgets are managed, how award winning creatives are crafted, and how client relationships develop into larger commercial commitments. I have supported senior leaders in growing accounts and have taken full solo ownership of new business when working independently, giving me a grounded, honest perspective on both sides of the commercial marketing world.\n\nFirst-class Honours degree in Marketing. Based in Lisbon, with an address in Dublin. Open to senior roles across Europe, with a preference for Lisbon based roles." },
    { key: 'looking_summary', label: "What I'm looking for — summary", multiline: true, def: "I am at a point in my career where I'm actively seeking the right environment over the right title. Whether that means stepping into a leadership role in a scale-up, or joining a high-calibre team as a strong senior contributor with a clear growth path. What is most important to me is the quality of the work, the people, and the opportunity to develop. I'm motivated by environments where marketing and commercial outcomes are tightly linked and where performance is what drives progression." },
    { key: 'section_who',      label: 'Section title — Who I Am',            def: 'Liam Boyd' },
    { key: 'section_done',     label: 'Section title — What I Have Done',    def: 'What I Have Done' },
    { key: 'section_looking',  label: "Section title — What I'm Looking For", def: "What I'm Looking For" },
    { key: 'section_standout', label: 'Section title — Why I Stand Out',     def: 'Why I Stand Out' },
    { key: 'footer_note',      label: 'Footer note',                         def: 'Full CV available on request · liam@liamboyd.com · +353 86 129 1030' },
  ],
};

function CopyPanel() {
  const [page, setPage]     = useState('home');
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    // Pre-fill with hardcoded defaults immediately
    const defaults = {};
    COPY_SCHEMA[page].forEach(({ key, def }) => { if (def !== undefined) defaults[key] = def; });
    setFields(defaults);
    // Overlay with any saved DB values
    supabase.from('site_settings').select('key, value').like('key', `copy_${page}_%`).then(({ data }) => {
      if (!data || data.length === 0) return;
      const saved = {};
      data.forEach(r => { saved[r.key.slice(`copy_${page}_`.length)] = r.value; });
      setFields(prev => ({ ...prev, ...saved }));
    });
  }, [page]);

  async function save() {
    setSaving(true);
    const schema = COPY_SCHEMA[page];
    await Promise.all(
      schema.map(({ key }) =>
        fields[key] !== undefined
          ? supabase.from('site_settings').upsert({ key: `copy_${page}_${key}`, value: fields[key] })
          : Promise.resolve()
      )
    );
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const schema = COPY_SCHEMA[page];

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--rule)', paddingBottom: '0.75rem' }}>
        {Object.keys(COPY_SCHEMA).map(p => (
          <button key={p} onClick={() => setPage(p)} style={{
            background: 'none', border: 'none', fontSize: '12px', cursor: 'pointer', textTransform: 'capitalize',
            color: page === p ? 'var(--ink)' : 'var(--ink-faint)',
            borderBottom: page === p ? '1px solid var(--ink)' : '1px solid transparent',
            paddingBottom: '0.2rem',
          }}>{p}</button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {schema.map(({ key, label, multiline, hint }) => (
          <div key={key}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '0.3rem' }}>
              {label}{hint && <span style={{ marginLeft: '0.5rem', opacity: 0.6 }}>— {hint}</span>}
            </label>
            {multiline ? (
              <textarea
                value={fields[key] ?? ''}
                onChange={e => setFields(f => ({ ...f, [key]: e.target.value }))}
                rows={4}
                style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }}
              />
            ) : (
              <input
                value={fields[key] ?? ''}
                onChange={e => setFields(f => ({ ...f, [key]: e.target.value }))}
                style={inp}
              />
            )}
          </div>
        ))}
      </div>

      {page === 'services' && <ServicesList />}
      {page === 'cv'       && <CvEditor />}
      {page === 'brief'    && <BriefEditor />}

      <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={save} disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1.25rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Saved ✓</span>}
      </div>
    </div>
  );
}

const DEFAULT_SERVICES = [
  { title: 'Commercial Editing',      desc: "Leading film editing projects from start to finish. Hand me the rushes and I'll bring your story to life." },
  { title: 'Creative Development',    desc: 'Leading creative pre-production from the ground up. Scripts, storyboards and shot lists crafted to sharpen your brief and set production up for success.' },
  { title: 'Production Support',      desc: 'Available to lead or support at any stage of a commercial production, from pre-production through to delivery.' },
  { title: 'Post Production Support', desc: 'Leading post-production efforts in-house or managing third-party post teams, keeping quality and deadlines on track.' },
];

function ServicesList() {
  const [items, setItems]   = useState(DEFAULT_SERVICES);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    supabase.from('site_settings').select('value').eq('key', 'services_list').single().then(({ data }) => {
      if (data?.value) setItems(JSON.parse(data.value));
    });
  }, []);

  function update(i, field, val) {
    setItems(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  }

  function add() {
    setItems(prev => [...prev, { title: '', desc: '' }]);
  }

  function remove(i) {
    setItems(prev => prev.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSaving(true);
    await supabase.from('site_settings').upsert({ key: 'services_list', value: JSON.stringify(items) });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--rule)' }}>
      <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '1rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Services List</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {items.map((s, i) => (
          <div key={i} style={{ border: '1px solid var(--rule)', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                value={s.title}
                onChange={e => update(i, 'title', e.target.value)}
                placeholder="Service title"
                style={{ ...inp, fontWeight: 500 }}
              />
              <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>×</button>
            </div>
            <textarea
              value={s.desc}
              onChange={e => update(i, 'desc', e.target.value)}
              placeholder="Description"
              rows={3}
              style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }}
            />
          </div>
        ))}
      </div>
      <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={add} style={{ ...inp, width: 'auto', padding: '0.5rem 1rem', cursor: 'pointer' }}>+ Add service</button>
        <button onClick={save} disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1.25rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save list'}
        </button>
        {saved && <span style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Saved ✓</span>}
      </div>
    </div>
  );
}

// ─── Careers defaults (used to pre-fill admin editors before DB save) ──────────
const ADMIN_CV_HIGHLIGHTS = [
  { stat: '€1M+',   desc: 'media budgets managed including multi-market European campaigns' },
  { stat: '2×',     desc: 'account media investment growth contributed to for new client account in approx. 6 months' },
  { stat: '~10+',   desc: 'European markets coordinated simultaneously as account manager across digital and traditional channels' },
  { stat: '~€200k', desc: 'new business revenue generated independently through outbound prospecting' },
  { stat: '20–50%', desc: 'added media value through structured supplier negotiation' },
  { stat: '€5–10k', desc: 'per production saved via in-house post-production capability' },
];
const ADMIN_CV_EXPERIENCE = [
  {
    role: 'Creative Producer', company: 'Banjoman Commercials', period: '2022 – Present', location: 'Dublin, Ireland',
    summary: "I've led end-to-end commercial production alongside independent new business development, taking full ownership of outreach, proposal development, post-production and client delivery.",
    bullets: [
      'Managed inbound briefs from agency partners, **independently leading competitive pitches**, creative development, and proposals through to conversion, then **owning full production and client management**',
      'Edited commercial films for broadcast and digital channels, with a focus on **delivering high-quality output with efficient workflows**, including social-first content and TVC edits with clearance and delivery management',
      '**Managed six-figure production budgets** across simultaneous broadcast and digital projects, with full accountability for delivery and margin',
      'Introduced in-house post-production capability using Adobe Premiere Pro, After Effects, and DaVinci Resolve, **generating €5–10k per project** in cost savings and incremental internal revenue',
      '**Negotiated third-party contracts** and production partnerships to protect margins and maintain quality standards',
      '**Advanced Adobe Creative Suite skills**, applied across the full production lifecycle, from TV-ready ad editing with clearance and delivery management, to social content, reversioning, graphics, and pitch documents. A genuine practical capability rare in marketing profiles at this level',
      '**Generated ~€200k in new business revenue** through targeted outbound prospecting (LinkedIn, Email, & phone), independently converting cold opportunities into client engagements',
      'Co-produced a TV-ready documentary series with production taking place in four countries (across EMEA and North America), managing all locations, stakeholders, cast, travel, and crew throughout',
    ],
  },
  {
    role: 'International Account Manager', company: 'Mediacom', period: '2019 – 2022', location: 'Dublin, Ireland',
    summary: "I've contributed to multi-market media strategy and commercial performance across a portfolio of national and international accounts, operating within teams managing seven-figure annual budgets.",
    bullets: [
      'Worked within teams managing annual investment portfolios of six to seven figures, including accounts acting as the central team across up to **10 European markets simultaneously**',
      '**Led go-to-market launch** for a new international streaming platform account and delivered results strong enough to set new EMEA benchmarks, directly contributing to the account budget doubling within 6–9 months',
      'Negotiated supplier agreements **delivering 20–50% added media value** through structured annual partnerships and rate optimisation',
      'Managed quarterly **multi-market campaign launches** for a leading international travel brand across digital and traditional channels',
      '**Managed and mentored 3–4 direct reports**; coordinated planning, buying, analytics, and creative stakeholders across accounts',
      '**Planned, managed and optimised performance campaigns** across paid social (Meta, LinkedIn, etc.) and programmatic display, with hands-on experience in social platforms, while search and programmatic were executed by specialist teams under strategic direction',
      'Delivered regular performance reports to **senior client stakeholders** and sales functions, including **campaign analysis, optimisation recommendations, and results against KPIs**',
      'Delivered forecasting models, ROI analysis, and executive performance reviews to senior leadership',
      'Supported **competitive retention pitches** and new business development across the wider agency portfolio',
    ],
  },
];
const ADMIN_CV_COMPETENCIES = [
  { area: 'Commercial',     skills: 'Account management · Budget stewardship (€1M+) · New business development · Commercial negotiation · Executive stakeholder engagement' },
  { area: 'Marketing',      skills: 'Multi-market campaign strategy (up to 10 markets) · Full-funnel performance optimisation · Go-to-market planning & execution · Media planning & buying · Creative production · Performance campaign management · Paid social · Programmatic display' },
  { area: 'Leadership',     skills: 'Direct line management (3–4 reports) · Cross-functional team coordination · Process design & governance · Consultative client engagement' },
  { area: 'Sector fluency', skills: 'Media & advertising · Commercial production · International travel · Entertainment · Agency environments' },
  { area: 'Creative tools', skills: 'Adobe Creative Suite (advanced) · Premiere Pro · After Effects · Photoshop · Illustrator · InDesign · DaVinci Resolve · Film editing & post-production · AI-assisted app and web building · Workflow automation · Prompt engineering' },
];
const ADMIN_CV_EDUCATION = { degree: 'B.Sc (Honours) in Marketing', institution: 'Technological University of Dublin', years: '2015 – 2019', grade: 'Grade: 1.1 (First Class Honours)' };

const ADMIN_BRIEF_DONE = [
  'Worked within teams managing **annual media investment portfolios of up to €1M+**, including campaigns across up to 10 European markets simultaneously at Mediacom',
  'Led go-to-market launch for a new international streaming platform account and delivered results strong enough to set new EMEA benchmarks, directly contributing to the **account budget doubling** within 6–9 months',
  'Negotiated supplier agreements **delivering 20–50% added media value** through structured annual partnerships',
  '**Independently generated ~€200k in new business** revenue at Banjoman through outbound prospecting and consultative proposal development',
  'Introduced in-house post-production capability **saving €5–10k per production** and creating an additional revenue stream',
  '**Managed and mentored teams of 3–4 direct reports** while coordinating multi-disciplinary stakeholders across planning, buying, analytics, and creative',
  'Produced an episodic documentary series spanning 2 continents and 4 countries, **managing full production logistics, and cross-territory coordination**',
  'Managed inbound briefs from agency partners, independently leading competitive pitches, creative development, and proposals through to conversion, then **owning full production, post-production, and client management** from award through to delivery',
  'Managed **performance marketing campaigns across paid social and programmatic display**, overseeing strategy, optimisation, and client reporting, with hands-on platform experience in social platforms',
];
const ADMIN_BRIEF_LOOKING_DETAILS = [
  { label: 'Titles that fit naturally', value: "Marketing Manager, Senior Marketing Manager, Head of Marketing (scale-up), Growth Manager, Commercial Marketing Lead, Senior Account Manager, Senior Marketing Executive. The title is less important than the opportunity." },
  { label: 'Industries',   value: "Open to all. I've worked within media and production contexts and managed client accounts across travel, finance, entertainment, & FMCG. Particularly drawn to founder-led businesses, scale-ups, and international growth environments where I can build something rather than maintain it." },
  { label: 'Geography',    value: 'Europe-focused. Ideally based in Lisbon. Open to remote, hybrid, or in-office. Open to travel.' },
  { label: 'Engagement',   value: 'Permanent role preferred, though open to conversations.' },
  { label: 'Availability', value: 'Immediately available.' },
];
const ADMIN_BRIEF_STANDOUT = [
  '**Unusually broad exposure** for my career stage across agency, client-direct, and production environments across multiple sectors and markets',
  '**Multi-market fluency** having contributed to campaigns across ~10 European markets with real commercial accountability at team level',
  '**Proven ability to generate new business** independently — the ~€200k at Banjoman was built entirely through my own outreach, with support structure only later in creative development',
  '**Strong execution track record** combined with growing strategic ambition — a profile suited to a role that offers real development headroom',
  '**Advanced Adobe Creative Suite skills**, applied across the full production lifecycle, from TV-ready ad editing with clearance and delivery management, to social content, reversioning, graphics, and pitch documents. A genuine practical capability rare in marketing profiles at this level',
  "Adept with AI tools — I've **built functional apps** and automated workflows using AI assistance, bringing a resourceful, build-it-yourself approach",
];

// ─── CV Editor ─────────────────────────────────────────────────────────────────
function moveArr(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const a = [...arr];
  [a[i], a[j]] = [a[j], a[i]];
  return a;
}

function ReorderBtns({ i, len, onMove }) {
  const btnStyle = disabled => ({
    background: 'none', border: '1px solid var(--rule)', fontSize: '9px', lineHeight: 1,
    padding: '2px 4px', cursor: disabled ? 'default' : 'pointer',
    color: disabled ? 'var(--rule)' : 'var(--ink-faint)',
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
      <button onClick={() => onMove(-1)} disabled={i === 0} style={btnStyle(i === 0)}>↑</button>
      <button onClick={() => onMove(1)} disabled={i === len - 1} style={btnStyle(i === len - 1)}>↓</button>
    </div>
  );
}

function CvEditor() {
  const [highlights,   setHighlights]   = useState(null);
  const [experience,   setExperience]   = useState(null);
  const [competencies, setCompetencies] = useState(null);
  const [education,    setEducation]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  useEffect(() => {
    supabase.from('site_settings').select('value').eq('key', 'cv_highlights').single().then(({ data }) => {
      setHighlights(data?.value ? JSON.parse(data.value) : ADMIN_CV_HIGHLIGHTS);
    });
    supabase.from('site_settings').select('value').eq('key', 'cv_experience').single().then(({ data }) => {
      setExperience(data?.value ? JSON.parse(data.value) : ADMIN_CV_EXPERIENCE);
    });
    supabase.from('site_settings').select('value').eq('key', 'cv_competencies').single().then(({ data }) => {
      setCompetencies(data?.value ? JSON.parse(data.value) : ADMIN_CV_COMPETENCIES);
    });
    supabase.from('site_settings').select('value').eq('key', 'cv_education').single().then(({ data }) => {
      setEducation(data?.value ? JSON.parse(data.value) : ADMIN_CV_EDUCATION);
    });
  }, []);

  async function saveCv() {
    setSaving(true);
    await Promise.all([
      supabase.from('site_settings').upsert({ key: 'cv_highlights',   value: JSON.stringify(highlights) }),
      supabase.from('site_settings').upsert({ key: 'cv_experience',   value: JSON.stringify(experience) }),
      supabase.from('site_settings').upsert({ key: 'cv_competencies', value: JSON.stringify(competencies) }),
      supabase.from('site_settings').upsert({ key: 'cv_education',    value: JSON.stringify(education) }),
    ]);
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  if (!highlights || !experience || !competencies || !education) return <p style={{ fontSize: '12px', color: 'var(--ink-faint)', marginTop: '2rem' }}>Loading…</p>;

  return (
    <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--rule)' }}>

      {/* Highlights */}
      <p style={cvSectionLabel}>Commercial Highlights</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        {highlights.map((h, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <ReorderBtns i={i} len={highlights.length} onMove={d => setHighlights(prev => moveArr(prev, i, d))} />
            <input value={h.stat} onChange={e => setHighlights(prev => prev.map((x, j) => j === i ? { ...x, stat: e.target.value } : x))}
              placeholder="Stat (e.g. €1M+)" style={{ ...inp, width: '100px' }} />
            <input value={h.desc} onChange={e => setHighlights(prev => prev.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))}
              placeholder="Description" style={inp} />
            <button onClick={() => setHighlights(prev => prev.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>
        ))}
        <button onClick={() => setHighlights(prev => [...prev, { stat: '', desc: '' }])}
          style={{ ...inp, width: 'auto', padding: '0.4rem 0.9rem', cursor: 'pointer', alignSelf: 'flex-start' }}>+ Add highlight</button>
      </div>

      {/* Experience */}
      <p style={{ ...cvSectionLabel, marginTop: '1.5rem' }}>Experience</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '1rem' }}>
        {experience.map((exp, i) => (
          <div key={i} style={{ border: '1px solid var(--rule)', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <ReorderBtns i={i} len={experience.length} onMove={d => setExperience(prev => moveArr(prev, i, d))} />
              <input value={exp.role} onChange={e => setExperience(prev => prev.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                placeholder="Role" style={{ ...inp, fontWeight: 500 }} />
              <input value={exp.period} onChange={e => setExperience(prev => prev.map((x, j) => j === i ? { ...x, period: e.target.value } : x))}
                placeholder="Period" style={{ ...inp, width: '120px' }} />
              <button onClick={() => setExperience(prev => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0 }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input value={exp.company} onChange={e => setExperience(prev => prev.map((x, j) => j === i ? { ...x, company: e.target.value } : x))}
                placeholder="Company" style={inp} />
              <input value={exp.location} onChange={e => setExperience(prev => prev.map((x, j) => j === i ? { ...x, location: e.target.value } : x))}
                placeholder="Location" style={inp} />
            </div>
            <textarea value={exp.summary} onChange={e => setExperience(prev => prev.map((x, j) => j === i ? { ...x, summary: e.target.value } : x))}
              placeholder="Summary (italic intro)" rows={2} style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
            <div>
              <p style={{ fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '0.4rem' }}>Bullets — use **text** for bold</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {(exp.bullets || []).map((b, bi) => (
                  <div key={bi} style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                    <ReorderBtns i={bi} len={exp.bullets.length} onMove={d => setExperience(prev => prev.map((x, j) => j === i ? { ...x, bullets: moveArr(x.bullets, bi, d) } : x))} />
                    <textarea value={b}
                      onChange={e => setExperience(prev => prev.map((x, j) => j === i ? { ...x, bullets: x.bullets.map((bb, bj) => bj === bi ? e.target.value : bb) } : x))}
                      rows={2} style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
                    <button onClick={() => setExperience(prev => prev.map((x, j) => j === i ? { ...x, bullets: x.bullets.filter((_, bj) => bj !== bi) } : x))}
                      style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0, paddingTop: '0.4rem' }}>×</button>
                  </div>
                ))}
                <button onClick={() => setExperience(prev => prev.map((x, j) => j === i ? { ...x, bullets: [...x.bullets, ''] } : x))}
                  style={{ ...inp, width: 'auto', padding: '0.3rem 0.75rem', cursor: 'pointer', alignSelf: 'flex-start', fontSize: '11px' }}>+ Add bullet</button>
              </div>
            </div>
          </div>
        ))}
        <button onClick={() => setExperience(prev => [...prev, { role: '', company: '', period: '', location: '', summary: '', bullets: [] }])}
          style={{ ...inp, width: 'auto', padding: '0.4rem 0.9rem', cursor: 'pointer', alignSelf: 'flex-start' }}>+ Add role</button>
      </div>

      {/* Competencies */}
      <p style={{ ...cvSectionLabel, marginTop: '1.5rem' }}>Core Competencies</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        {competencies.map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <ReorderBtns i={i} len={competencies.length} onMove={d => setCompetencies(prev => moveArr(prev, i, d))} />
            <input value={c.area} onChange={e => setCompetencies(prev => prev.map((x, j) => j === i ? { ...x, area: e.target.value } : x))}
              placeholder="Area" style={{ ...inp, width: '130px', flexShrink: 0 }} />
            <textarea value={c.skills} onChange={e => setCompetencies(prev => prev.map((x, j) => j === i ? { ...x, skills: e.target.value } : x))}
              placeholder="Skills (· separated)" rows={2} style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
            <button onClick={() => setCompetencies(prev => prev.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0, paddingTop: '0.5rem' }}>×</button>
          </div>
        ))}
        <button onClick={() => setCompetencies(prev => [...prev, { area: '', skills: '' }])}
          style={{ ...inp, width: 'auto', padding: '0.4rem 0.9rem', cursor: 'pointer', alignSelf: 'flex-start' }}>+ Add competency</button>
      </div>

      {/* Education */}
      <p style={{ ...cvSectionLabel, marginTop: '1.5rem' }}>Education</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {['degree', 'institution', 'years', 'grade'].map(field => (
          <input key={field} value={education[field]} onChange={e => setEducation(prev => ({ ...prev, [field]: e.target.value }))}
            placeholder={field.charAt(0).toUpperCase() + field.slice(1)} style={inp} />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={saveCv} disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1.25rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save CV data'}
        </button>
        {saved && <span style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Saved ✓</span>}
      </div>
    </div>
  );
}

// ─── Brief Editor ──────────────────────────────────────────────────────────────
function BriefEditor() {
  const [done,           setDone]           = useState(null);
  const [lookingDetails, setLookingDetails] = useState(null);
  const [standout,       setStandout]       = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    supabase.from('site_settings').select('value').eq('key', 'brief_done').single().then(({ data }) => {
      setDone(data?.value ? JSON.parse(data.value) : ADMIN_BRIEF_DONE);
    });
    supabase.from('site_settings').select('value').eq('key', 'brief_looking_details').single().then(({ data }) => {
      setLookingDetails(data?.value ? JSON.parse(data.value) : ADMIN_BRIEF_LOOKING_DETAILS);
    });
    supabase.from('site_settings').select('value').eq('key', 'brief_standout').single().then(({ data }) => {
      setStandout(data?.value ? JSON.parse(data.value) : ADMIN_BRIEF_STANDOUT);
    });
  }, []);

  async function saveBrief() {
    setSaving(true);
    await Promise.all([
      supabase.from('site_settings').upsert({ key: 'brief_done',            value: JSON.stringify(done) }),
      supabase.from('site_settings').upsert({ key: 'brief_looking_details', value: JSON.stringify(lookingDetails) }),
      supabase.from('site_settings').upsert({ key: 'brief_standout',        value: JSON.stringify(standout) }),
    ]);
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  if (!done || !lookingDetails || !standout) return <p style={{ fontSize: '12px', color: 'var(--ink-faint)', marginTop: '2rem' }}>Loading…</p>;

  return (
    <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--rule)' }}>

      {/* What I Have Done */}
      <p style={cvSectionLabel}>What I Have Done — bullets (use **text** for bold)</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
        {done.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <ReorderBtns i={i} len={done.length} onMove={d => setDone(prev => moveArr(prev, i, d))} />
            <textarea value={item} onChange={e => setDone(prev => prev.map((x, j) => j === i ? e.target.value : x))}
              rows={2} style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
            <button onClick={() => setDone(prev => prev.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0, paddingTop: '0.5rem' }}>×</button>
          </div>
        ))}
        <button onClick={() => setDone(prev => [...prev, ''])}
          style={{ ...inp, width: 'auto', padding: '0.4rem 0.9rem', cursor: 'pointer', alignSelf: 'flex-start' }}>+ Add bullet</button>
      </div>

      {/* Looking For — details */}
      <p style={{ ...cvSectionLabel, marginTop: '1.5rem' }}>What I'm Looking For — details</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
        {lookingDetails.map((item, i) => (
          <div key={i} style={{ border: '1px solid var(--rule)', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <ReorderBtns i={i} len={lookingDetails.length} onMove={d => setLookingDetails(prev => moveArr(prev, i, d))} />
              <input value={item.label} onChange={e => setLookingDetails(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                placeholder="Label" style={{ ...inp, fontWeight: 500 }} />
              <button onClick={() => setLookingDetails(prev => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0 }}>×</button>
            </div>
            <textarea value={item.value} onChange={e => setLookingDetails(prev => prev.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
              placeholder="Value" rows={2} style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
          </div>
        ))}
        <button onClick={() => setLookingDetails(prev => [...prev, { label: '', value: '' }])}
          style={{ ...inp, width: 'auto', padding: '0.4rem 0.9rem', cursor: 'pointer', alignSelf: 'flex-start' }}>+ Add item</button>
      </div>

      {/* Why I Stand Out */}
      <p style={{ ...cvSectionLabel, marginTop: '1.5rem' }}>Why I Stand Out — bullets (use **text** for bold)</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.5rem' }}>
        {standout.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <ReorderBtns i={i} len={standout.length} onMove={d => setStandout(prev => moveArr(prev, i, d))} />
            <textarea value={item} onChange={e => setStandout(prev => prev.map((x, j) => j === i ? e.target.value : x))}
              rows={2} style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
            <button onClick={() => setStandout(prev => prev.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--ink-faint)', cursor: 'pointer', flexShrink: 0, paddingTop: '0.5rem' }}>×</button>
          </div>
        ))}
        <button onClick={() => setStandout(prev => [...prev, ''])}
          style={{ ...inp, width: 'auto', padding: '0.4rem 0.9rem', cursor: 'pointer', alignSelf: 'flex-start' }}>+ Add bullet</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={saveBrief} disabled={saving} style={{ ...inp, width: 'auto', padding: '0.5rem 1.25rem', background: 'var(--ink)', color: 'white', border: 'none', cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save Brief data'}
        </button>
        {saved && <span style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Saved ✓</span>}
      </div>
    </div>
  );
}

const cvSectionLabel = { fontSize: '11px', color: 'var(--ink-faint)', marginBottom: '0.5rem', letterSpacing: '0.06em', textTransform: 'uppercase' };
